import { useCallback, useEffect, useRef, useState } from "react";
import { canLookupTaxTable, lookupTaxTable } from "./staffTaxTableLookup.logic";

interface TaxTableLookupParams {
  companyId: number | null | undefined;
  personalIdentityNumber: string | null;
  onFound: (taxTable: string) => void;
  onMissing?: () => void;
  onFailed?: () => void;
}

interface UseTaxTableLookupResult {
  readonly isLoading: boolean;
  readonly cancel: () => void;
  readonly lookup: (params: TaxTableLookupParams) => void;
}

/**
 * Owns a staff form's tax table lookup: its in-flight state, and the request id
 * that decides whether an answer still matters. Every way an answer can stop
 * mattering — a newer lookup, a closed form, an unmounted one — is the same
 * thing here, a bumped id, so a late response simply finds itself stale and
 * touches nothing.
 *
 * Only `onFound` is required: a form that looks the table up on its own,
 * without the user asking, stays silent when nothing is registered rather than
 * nagging about it. The table is never written anywhere but the field the
 * caller chooses, and a lookup that finds nothing leaves that field as it was.
 */
export const useTaxTableLookup = (): UseTaxTableLookupResult => {
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);

  /** Abandons whatever is in flight, for a form being closed or reset. */
  const cancel = useCallback((): void => {
    requestIdRef.current += 1;
    setIsLoading(false);
  }, []);

  useEffect(() => {
    // Unmounting only bumps the id: the pending answer goes stale and returns
    // before it can set state on a component that is no longer there.
    return () => {
      requestIdRef.current += 1;
    };
  }, []);

  const lookup = useCallback((params: TaxTableLookupParams): void => {
    if (params.companyId == null) return;
    if (!canLookupTaxTable(params.personalIdentityNumber)) return;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsLoading(true);

    const isOutdated = (): boolean => requestIdRef.current !== requestId;

    void lookupTaxTable({
      companyId: params.companyId,
      personalIdentityNumber: params.personalIdentityNumber ?? "",
    })
      .then((foundTaxTable) => {
        if (isOutdated()) return;
        if (foundTaxTable == null) {
          params.onMissing?.();
          return;
        }
        params.onFound(String(foundTaxTable));
      })
      .catch(() => {
        if (isOutdated()) return;
        params.onFailed?.();
      })
      .finally(() => {
        if (isOutdated()) return;
        setIsLoading(false);
      });
  }, []);

  return { isLoading, cancel, lookup };
};
